<template>
  <div class="item-master-tags-docs fh fw">
    <div class="item-master-tags-docs__card">
      <div class="item-master-tags-docs__title">
        <img class="item-master-tags-docs__icon" src="@/assets/images/icons/tag-icon.svg" alt="Tags" />
        <span>Tags</span>
      </div>
      <div class="item-master-tags-docs__tag-groups">
        <div
          v-for="group in tagGroups"
          :key="`tag-group-${group.root.id}`"
          class="item-master-tags-docs__tag-group"
        >
          <div class="item-master-tags-docs__tags">
            <template v-for="(tag, index) in group.tags">
              <span
                :key="`${group.root.id}-${tag.id}-${index}-name`"
                class="item-master-tags-docs__tag"
              >
                {{ tag.name }}
              </span>
              <span
                v-if="index < group.tags.length - 1"
                :key="`${group.root.id}-${tag.id}-${index}-separator`"
                class="item-master-tags-docs__tag-separator"
              >
                &gt;
              </span>
            </template>
          </div>
        </div>
        <span v-if="tagGroups.length === 0" class="item-master-tags-docs__empty">No tags attached</span>
      </div>
    </div>

    <div class="item-master-tags-docs__card">
      <div class="item-master-tags-docs__title">
        <img class="item-master-tags-docs__icon" src="@/assets/images/icons/attachment-icon.svg" alt="Documents" />
        <span>Attached Documents</span>
      </div>
      <div class="item-master-tags-docs__docs">
        <div v-for="doc in documents" :key="doc.id" class="item-master-tags-docs__doc">
          <div class="item-master-tags-docs__doc-icon pointer" @click.stop="openDocument(doc)">
            <img  v-if="logoMap[doc.S3Key]" :src="logoMap[doc.S3Key]"  alt="Document" />
            <img  v-else src="@/assets/images/icons/file-icon.svg"  alt="Document" />
          </div>
          <div class="item-master-tags-docs__doc-info">
            <div class="item-master-tags-docs__doc-name">{{ doc.name }}</div>
            <div class="item-master-tags-docs__doc-meta">
              {{ doc.meta }}
            </div>
          </div>
          <button class="item-master-tags-docs__doc-open" v-tooltip="'Open Document'" @click.stop="openDetail(doc)">
            <img src="@/assets/images/icons/tab-open.svg" alt="Open" />
          </button>
        </div>
        <div v-if="documents?.length === 0" class="item-master-tags-docs__empty">
          No documents attached
        </div>
      </div>
    </div>

    <div class="item-master-tags-docs__card">
      <div class="item-master-tags-docs__title item-master-tags-docs__title--between">
        <div class="item-master-tags-docs__title-left">
          <img class="item-master-tags-docs__icon" src="@/assets/images/icons/forms-doc.svg" alt="Associated Form" />
          <span>Associated Form</span>
        </div>
        <span v-if="formStatusLabel" class="item-master-tags-docs__status" :class="formStatusClass">
          {{ formStatusLabel }}
        </span>
      </div>
      <div v-if="materialMaster?.core_form">
        <skeleton v-if="formLoading" :rows="3" />
        <template v-else>
          <div class="item-master-tags-docs__form-card">
            <div class="item-master-tags-docs__form-header">
              <div class="item-master-tags-docs__form-title">
                <span class="name">{{ formDisplayName }}</span>
              </div>
            </div>
            <hr class="item-master-tags-docs__form-separator" />
            <div class="item-master-tags-docs__form-meta">
              <span>Created by : {{ formCreatedBy }}</span>
              <span> • </span>
              <span>Created on : {{ formCreatedOn }}</span>
              <span> • </span>
              <span>Modified : {{ formUpdatedOn }}</span>
            </div>
          </div>
          <div class="item-master-tags-docs__form-grid">
          <template v-for="field in filteredTemplateFields">
            <div v-if="field?.form_field?.key === 'TABLE'"
            :key="`${field.field_id}-table`"
            class="item-master-tags-docs__table-full"
            >
      <TableTemplate
        :formTemplate="true"
        :fieldId="field.field_id"
        :data="field"
        :value="materialCustomFieldData[field.field_id]?.value"
        mode="VIEW"
      />
    </div>
   <div
      v-else
      :key="`${field.field_id}-field`"
      class="item-master-tags-docs__form-field"
      :class="{
        'item-master-tags-docs__form-field--full': isLongFormValue(field)
      }"
    >
      <span class="label">
        {{ field?.caption ?? '--' }}
      </span>
      <span class="value">
        {{ materialCustomFieldData[field.field_id]?.value ?? '--' }}
      </span>
    </div>
  </template>
</div>
        </template>
      </div>
      <div v-else class="item-master-tags-docs__empty">
        No forms data available
      </div>
    </div>
    <attached-doc-view v-if="detailObject.open" :linkedDocs="detailObject.linkedDocs" @close="closeDetail" />
  </div>
</template>

<script>
import { generateS3DownloadingUrl, GetFormDataByFormId } from '@/api'
import config from '@/config'
import Skeleton from '@/components/common/skeleton.vue'
import AttachedDocView from '@/components/document/attachedDocView.vue'
import TableTemplate from '@/views/SETTINGS/tableTemplate.vue'
export default {
  name: 'ItemMasterTagsDocumentsSection',
  components: { Skeleton, AttachedDocView, TableTemplate },
  props: {
    materialMaster: {
      type: Object,
      default: () => ({})
    }

  },
  data () {
    return {
      documents: [],
      logoMap: {},
      templateFields: [],
      materialCustomFieldData: {},
      formLoading: false,
      formMeta: {},
      detailObject: {
        open: false,
        linkedDocs: []
      }
    }
  },
  computed: {
    tagItems () {
      const uniqueTags = new Map()

      ;(this.materialMaster.tag_materials || []).forEach((item) => {
        const tag = item?.tag
        if (!tag?.id || !tag?.name || uniqueTags.has(tag.id)) return
        uniqueTags.set(tag.id, {
          id: tag.id,
          name: tag.name,
          parentId: tag.parent_id ?? null
        })
      })

      return [...uniqueTags.values()]
    },
    tagGroups () {
      if (!this.tagItems.length) return []

      const tagMap = new Map(this.tagItems.map((item) => [item.id, item]))

      const findRootId = (tag) => {
        let current = tag
        const seen = new Set([tag.id])

        while (
          current?.parentId !== null &&
          current?.parentId !== undefined &&
          tagMap.has(current.parentId) &&
          !seen.has(current.parentId)
        ) {
          current = tagMap.get(current.parentId)
          seen.add(current.id)
        }

        return current?.id ?? tag.id
      }

      const getDepth = (tag) => {
        let depth = 0
        let current = tag
        const seen = new Set([tag.id])

        while (
          current?.parentId !== null &&
          current?.parentId !== undefined &&
          tagMap.has(current.parentId) &&
          !seen.has(current.parentId)
        ) {
          current = tagMap.get(current.parentId)
          seen.add(current.id)
          depth++
        }

        return depth
      }

      const groupedMap = new Map()

      this.tagItems.forEach((tag) => {
        const rootId = findRootId(tag)
        const existing = groupedMap.get(rootId) || []
        existing.push(tag)
        groupedMap.set(rootId, existing)
      })

      return [...groupedMap.entries()].map(([rootId, tags]) => {
        const rootTag = tagMap.get(rootId) || tags[0]
        const orderedTags = [...tags].sort((a, b) => {
          const depthDiff = getDepth(a) - getDepth(b)
          if (depthDiff !== 0) return depthDiff
          return a.name.localeCompare(b.name)
        })

        return {
          root: rootTag,
          tags: orderedTags
        }
      }).sort((a, b) => a.root.name.localeCompare(b.root.name))
    },
    filteredTemplateFields () {
      return (this.templateFields || []).filter((item) => !item.autogenerated)
    },
    formDisplayName () {
      return this.formMeta?.templateName || 'Associated Form'
    },
    formCreatedBy () {
      return this.formMeta?.createdBy || '--'
    },
    formCreatedOn () {
      return this.formMeta?.createdOn || '--'
    },
    formUpdatedOn () {
      return this.formMeta?.updatedOn || '--'
    },
    canViewForm () {
      return Boolean(this.formMeta?.templateId && this.formMeta?.id)
    },
    formStatusLabel () {
      const status = this.formMeta?.status
      if (!status) return ''
      return config.FORM_STATE?.[status] || 'ACTIVE'
    },
    formStatusClass () {
      const status = this.formMeta?.status
      if (!status) return ''
      if ([config.FORM_STATE_MAP.OPEN, config.FORM_STATE_MAP.IN_PROGRESS, config.FORM_STATE_MAP.REOPENED].includes(status)) {
        return 'status-active'
      }
      if ([config.FORM_STATE_MAP.DRAFT].includes(status)) {
        return 'status-draft'
      }
      return 'status-closed'
    }
  },
  methods: {
    getEffectiveDocumentVersion (doc) {
      if (doc?.core_document_version) return doc.core_document_version

      const coreDoc = doc?.core_document || {}
      const versions = Array.isArray(coreDoc.associated_versions) ? coreDoc.associated_versions : []
      const targetId = doc?.document_id || doc?.version_id

      if (targetId) {
        if (coreDoc?.id === targetId) return coreDoc
        const matchedVersion = versions.find((item) => item?.id === targetId)
        if (matchedVersion) return matchedVersion
      }
      if (versions.length > 0) {
        return versions.at(-1)
      }

      return coreDoc
    },
    loadDocumentThumbnails () {
      const S3Objects = []
      this.documents = (this.materialMaster.material_document_associations || []).map((doc) => {
        const effectiveDoc = this.getEffectiveDocumentVersion(doc)
        const name = effectiveDoc?.doc_name || doc?.core_document?.doc_name || 'Untitled document'
        const type = effectiveDoc?.doc_ext?.toUpperCase() || doc?.core_document?.doc_ext?.toUpperCase() || 'File'
        const attachedUser = `${doc?.core_user?.first_name || ''} ${doc?.core_user?.last_name || ''}`.trim() || 'Unknown'
        const rawSizeKb = effectiveDoc?.doc_size || doc?.core_document?.doc_size || 0
        const size = this.formatFileSizeFromKb(rawSizeKb)
        // const owner = doc.core_document?.uploaded_by?.name || 'Unknown'
        const date = doc?.created_on
          ? new Date(doc?.created_on).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
          : 'Date unavailable'
        const thumbnailKey = effectiveDoc?.thumbnail_blob_key || doc?.core_document?.thumbnail_blob_key

        if (thumbnailKey) {
          S3Objects.push({
            fileName: name,
            S3Key: thumbnailKey
          })
        }

        return {
          id: effectiveDoc?.id || doc.id || doc.document_id || name,
          document_id: doc.document_id || doc.id,
          name,
          meta: `${type} - ${size} - by ${attachedUser} - ${date}`,
          S3Key: thumbnailKey

        }
      })

      S3Objects.length > 0 && this.loadThumbnails(S3Objects)
    },
    getMaterialCustomFieldData () {
      if (!this.materialMaster?.core_form?.id) {
        this.templateFields = []
        this.materialCustomFieldData = {}
        this.formMeta = {}
        return
      }

      this.formLoading = true
      this.templateFields = this.materialMaster?.core_form?.template_version?.template_fields || []
      this.materialCustomFieldData = {}
      GetFormDataByFormId(this.materialMaster?.core_form?.id, false).then((res) => {
        const form = res?.core_forms_by_pk || {}
        const createdBy = form.created_by_user
          ? `${form.created_by_user.first_name} ${form.created_by_user.last_name}`
          : '--'
        const updatedBy = form.updated_by_user
          ? `${form.updated_by_user.first_name} ${form.updated_by_user.last_name}`
          : '--'
        this.formMeta = {
          id: form.id,
          status: form.status,
          sequenceValue: form.sequence_value,
          templateId: form.template_version?.core_form_template?.id || form.template_version?.template_id,
          templateName: form.template_version?.core_form_template?.name,
          templateType: form.template_version?.core_form_template?.core_form_type?.name,
          createdBy: createdBy !== '--' ? createdBy : updatedBy,
          createdOn: this.formatDate(form.created_on),
          updatedOn: this.formatDate(form.updated_on)
        }
        res.core_forms_by_pk.forms_metadata_by_id.forEach((item) => {
          this.materialCustomFieldData[item?.field_id] = {
            id: item.field_id,
            value: item.string_value ??
                    item.time_value?.split('+')[0] ??
                    item.int_value ??
                    item.date_value ??
                    item.bool_value ??
                    item.point_value ??
                    item.string_value ??
                    '--'
          }
        })
        res.core_forms_by_pk.forms_table.forEach((item) => {
          this.materialCustomFieldData[item.field_id] = {
            field_id: item.field_id,
            value: item?.table_data
          }
        })
        res.core_forms_by_pk.forms_config_lists.forEach((item) => {
          this.materialCustomFieldData[item.field_id] = {
            field_id: item.field_id,
            value: item?.form_config_list.map(listItem => listItem.value).join(' -> ')
          }
        })
        this.materialCustomFieldData = { ...this.materialCustomFieldData }
      }).catch((err) => {
        console.log(err)
      }).finally(() => {
        this.formLoading = false
      })
    },
    viewForm () {
      const route = this.$router.resolve({
        path: `/forms/template/${this.formMeta.templateId}/form/${this.formMeta.id}/view`
      })
      window.open(route.href, '_blank')
    },
    isLongFormValue (field) {
      const rawValue = this.materialCustomFieldData?.[field?.field_id]?.value
      if (rawValue === null || rawValue === undefined) return false
      const value = String(rawValue)
      if (value.includes('\n')) return true
      return value.length > 80
    },
    formatDate (value) {
      if (!value) return '--'
      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })
    },
    formatFileSizeFromKb (kbValue) {
      const value = Number(kbValue)

      if (!Number.isFinite(value) || value <= 0) return '0 KB'

      const units = ['KB', 'MB', 'GB', 'TB']
      let size = value
      let unitIndex = -1

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024
        unitIndex++
      }

      let display = ''
      if (size >= 100) {
        display = Math.round(size).toString()
      } else if (size >= 10) {
        display = size.toFixed(1)
      } else {
        display = size.toFixed(2)
      }

      // Remove trailing zeros
      display = display.replace(/\.0+$|(\.\d*[1-9])0+$/, '$1')
      return `${display} ${units[unitIndex]}`
    },
    loadThumbnails (S3Objects) {
      generateS3DownloadingUrl({
        S3Objects: S3Objects
      }).then((res) => {
        this.logoMap = {}
        for (const thumbnail of res.url) {
          this.logoMap[thumbnail.S3Key] = thumbnail.url
        }
      }).catch((err) => {
        console.log(err)
      })
    },
    openDocument (doc) {
      this.detailObject = {
        open: true,
        linkedDocs: [{
          id: doc.id,
          document_id: doc.document_id
        }]
      }
    },
    openDetail (doc) {
      this.openDocument(doc)
    },
    closeDetail () {
      this.detailObject = {
        open: false,
        linkedDocs: []
      }
    }
  },
  mounted () {
    this.loadDocumentThumbnails()
    this.getMaterialCustomFieldData()
  },
  watch: {
    'materialMaster.id' () {
      this.loadDocumentThumbnails()
      this.getMaterialCustomFieldData()
    }
  }
}
</script>

<style lang="scss" scoped>
.item-master-tags-docs__table-full {
  grid-column: 1 / -1;
  width: 100%;
}
.item-master-tags-docs {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 32px 8px 32px;

  &__card {
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    padding: 20px 22px;
    background: #ffffff;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-weight: 400;
    color: #0f172a;
    font-size: var(--text-s);
    margin-bottom: 14px;
  }
  &__title--between {
    justify-content: space-between;
  }
  &__title-left {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__status {
    font-size: 11px;
    font-weight: 400;
    padding: 3px 10px;
    border-radius: 999px;
    border: 1px solid transparent;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .status-active {
    color: #0f6f2a;
    background: #e8fbef;
    border-color: #b7f2c5;
  }

  .status-draft {
    color: #92400e;
    background: #fff7ed;
    border-color: #fed7aa;
  }

  .status-closed {
    color: #6b7280;
    background: #f3f4f6;
    border-color: #e5e7eb;
  }

  &__icon {
    width: 15px;
    height: 15px;
    filter: invert(54%) sepia(48%) saturate(531%) hue-rotate(350deg) brightness(90%) contrast(90%);
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 4px 10px;
    border-radius: 8px;
    background: var(--brand-light-color);
    color: var(--brand-color);
    font-size: var(--text-xs2);
    font-weight: 400;
    border: 1px solid var(--brand-color);
    width: fit-content;
    max-width: 100%;
  }
  &__tag-groups {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  &__tag-group {
    border: 1px dashed #e2e8f0;
    border-radius: 10px;
    padding: 10px;
    background: #f8fafc;
  }
  &__tag {
    overflow-wrap: anywhere;
  }
  &__tag-separator {
    color: var(--brand-color);
  }

  &__docs {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__doc {
    display: flex;
    gap: 12px;
    align-items: center;
    border: 1px solid var(--brand-color);
    border-radius: 12px;
    padding: 12px 14px;
    background: #f8fafc;
  }
  &__doc-open {
    border: 1px solid var(--brand-color);
    background: #ffffff;
    border-radius: 8px;
    padding: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    img {
      width: 14px;
      height: 14px;
    }
  }

  &__doc-icon {
    width: 35px;
    height: 35px;
    border-radius: 6px;
    background: #e0ecff;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
max-height: 30px;
max-width: 30px;
    }
  }

  &__doc-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__doc-name {
    font-weight: 500;
    font-size: var(--text-xs2);
    color: #0f172a;
  }

  &__doc-meta {
    color: #64748b;
    font-size: var(--text-xs1);
  }

  &__doc-action {
    margin-left: auto;
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #0f172a;
    font-size: 11px;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
    white-space: nowrap;
  }

  &__doc-action:hover {
    background: #f8fafc;
  }

  &__empty {
    color: #94a3b8;
    font-size: var(--text-xs2);
  }

  &__form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 16px;
    margin-top: 12px;
  }

  &__form-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 10px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    background: #fafafa;
    min-width: 0;

    .label {
      color: #8a8e97;
      font-size: var(--text-xs1);
      font-weight: 400;
    }

    .value {
      color: #0f172a;
      font-size: var(--text-xs2);
      font-weight: 400;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }
  &__form-field--full {
    grid-column: 1 / -1;
  }

  &__form-card {
    border: 1px solid #86efac;
    background: #f0fdf4;
    border-radius: 12px;
    padding: 14px 16px;
  }

  &__form-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__form-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #0f172a;
    font-weight: 500;
    font-size: var(--text-xs2);

    .name {
      font-weight: 400;
    }
  }

  &__badge {
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 6px;
    background: #dcfce7;
    color: #166534;
    border: 1px solid #86efac;
    font-weight: 400;
  }

  &__form-action {
    border: 1px solid #e5e7eb;
    background: #ffffff;
    color: #0f172a;
    font-size: 11px;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;
  }

  &__form-action:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &__form-separator {
      border: 1px solid #86efad5a;
      transform: scaleY(0.5);
  }

  &__form-meta {
    margin-top: 10px;
    font-size: 10px;
    color: #6b7280;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  &__table-full {
  grid-column: 1 / -1;
  width: 100%;
  min-width: 0;
}

}

@media (max-width: 768px) {
  .item-master-tags-docs {
    padding: 20px;
  }

  .item-master-tags-docs__form-grid {
    grid-template-columns: 1fr;
  }
}

</style>
